import React, { useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { FiDownload, FiUpload } from "react-icons/fi";
import { IoAlertCircle } from "react-icons/io5";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/Auth";
import { BASE_URL } from "../../../utils/endpoints";


const BulkSupplierUploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
    const { requestWithAuth } = useAuth();
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [deactivateButton, setDeactivateButton] = useState(false);

    const handleFileSelect = (selectedFile) => {
        if (selectedFile && selectedFile.name.endsWith(".xlsx")) {
            setFile(selectedFile);
            setUploadResult(null);
        } else {
            alert("Please select a valid Excel file (.xlsx)");
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            handleFileSelect(droppedFile);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error("Please select a file first");
            return;
        }

        setIsUploading(true);
        const formData = new FormData();
        formData.append("suppliers_list", file);
        formData.append("upload_type", "bulk");

        try {
            const response = await requestWithAuth(
                "/supplier/bulk-creation/",
                "POST",
                formData,
                {
                    "Content-Type": "mulitipart/form-data",
                }
            );

            setUploadResult(response);
            setDeactivateButton(true);

        } catch (error) {
            setUploadResult({
                error: "Network error occurred while uploading",
                upload_type: "bulk",
            });
        } finally {
            setIsUploading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full m-4 max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between p-6 border-b">
                    <div className="flex items-center space-x-3">
                        <h2 className="text-xl font-semibold text-gray-900">
                            Bulk Supplier Upload
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <span className="h-6 w-6">X</span>
                    </button>
                </div>

                {/* content */}
                <div className="p-6 overflow-y-auto flex-1">
                    <div className="mb-6">
                        <h3 className="text-sm font-medium text-gray-900 mb-2">
                            Instructions:
                        </h3>
                        <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Upload an Excel file (.xlsx) with supplier information</li>
                            <li>• Download and use the template provided </li>
                            <li>
                                • Required columns: name
                            </li>
                            <li>• All suppliers will be created or none </li>
                        </ul>
                    </div>

                    {/* DOWNLOAD TEMPLATE */}
                    <div className="mb-6">
                        <a
                            href={`${BASE_URL}/media/CompanyStock.xlsx`}
                            download={"supplier_upload_template.xlsx"}
                            target="_blank"
                            className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                            <FiDownload className="h-4 w-4" />
                            <span>Download Template</span>
                        </a>
                    </div>

                    {/* FILE UPLOAD AREA */}
                    <div
                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
                            ? "border-blue-400 bg-blue-50"
                            : file
                                ? "border-green-400 bg-green-50"
                                : "border-gray-300 hover:border-gray-400"
                            }`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                    >
                        {file ? (
                            <div className="space-y-2">
                                <FaCheckCircle className="h-12 w-12 text-green-500 mx-auto" />
                                <p className="text-sm font-medium text-green-700">
                                    {file.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {(file.size / 1024).toFixed(1)} KB
                                </p>
                                <button
                                    onClick={() => setFile(null)}
                                    className="text-xs text-red-600 hover:text-red-800"
                                >
                                    Remove file
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <FiUpload className="h-12 w-12 text-gray-400 mx-auto" />
                                <p className="text-sm text-gray-600">
                                    Drag and drop your Excel file here, or{" "}
                                    <label className="text-blue-600 hover:text-blue-800 cursor-pointer font-medium">
                                        browse
                                        <input
                                            type="file"
                                            accept=".xlsx"
                                            onChange={(e) => handleFileSelect(e.target.files[0])}
                                            className="hidden"
                                        />
                                    </label>
                                </p>
                                <p className="text-xs text-gray-500">
                                    Only .xlsx files are supported
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Upload Results */}
                    {uploadResult && (
                        <div className="mt-6 space-y-4">
                            {/* Summary Statistics */}
                            <div className={`border rounded-lg p-4 ${uploadResult.error ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'
                                }`}>
                                <div className="flex items-start space-x-3">
                                    {uploadResult.error ? (
                                        <IoAlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                                    ) : (
                                        <FaCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                                    )}
                                    <div className="flex-1">
                                        <h4 className={`text-sm font-medium ${uploadResult.error ? 'text-yellow-800' : 'text-green-800'
                                            }`}>
                                            {uploadResult.error ? 'Upload Completed with Errors' : 'Upload Successful'}
                                        </h4>
                                        <div className="mt-2 grid grid-cols-3 gap-4 text-sm">
                                            <div>
                                                <p className="text-gray-600">Total Processed</p>
                                                <p className="font-semibold text-gray-900">{uploadResult.total_processed || 0}</p>
                                            </div>
                                            <div>
                                                <p className="text-green-600">Successful</p>
                                                <p className="font-semibold text-green-700">{uploadResult.successful_count || 0}</p>
                                            </div>
                                            <div>
                                                <p className="text-red-600">Failed</p>
                                                <p className="font-semibold text-red-700">{uploadResult.failed_count || 0}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Failed Suppliers List */}
                            {uploadResult.failed_suppliers && uploadResult.failed_suppliers.length > 0 && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <div className="flex items-start justify-between mb-3">
                                        <h4 className="text-sm font-medium text-red-800">
                                            Failed Suppliers ({uploadResult.failed_suppliers.length})
                                        </h4>
                                        <button
                                            onClick={() => {
                                                const failedData = uploadResult.failed_suppliers.map(supplier => {
                                                    const errorMsg = typeof supplier.error === 'string'
                                                        ? supplier.error
                                                        : JSON.stringify(supplier.error);
                                                    return `${supplier.name}: ${errorMsg}`;
                                                }).join('\n');
                                                navigator.clipboard.writeText(failedData);
                                                toast.success('Failed suppliers list copied to clipboard');
                                            }}
                                            className="text-xs text-red-600 hover:text-red-800 font-medium"
                                        >
                                            Copy List
                                        </button>
                                    </div>
                                    <div className="max-h-96 overflow-y-auto bg-white rounded border border-red-100 p-3">
                                        <ul className="text-xs text-red-700 space-y-2">
                                            {uploadResult.failed_suppliers.map((supplier, index) => {
                                                // Handle error being either a string or an object
                                                let errorDisplay;
                                                if (typeof supplier.error === 'string') {
                                                    errorDisplay = supplier.error;
                                                } else if (typeof supplier.error === 'object') {
                                                    // Format object errors (e.g., {reorder_level: ["This field may not be null."]})
                                                    errorDisplay = Object.entries(supplier.error)
                                                        .map(([field, messages]) => {
                                                            const msgArray = Array.isArray(messages) ? messages : [messages];
                                                            return `${field}: ${msgArray.join(', ')}`;
                                                        })
                                                        .join('; ');
                                                } else {
                                                    errorDisplay = 'Unknown error';
                                                }

                                                return (
                                                    <li key={index} className="pb-2 border-b border-red-100 last:border-0">
                                                        <div className="font-medium text-red-900">{supplier.name}</div>
                                                        <div className="text-red-600 mt-0.5">{errorDisplay}</div>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {!deactivateButton ? (
                    <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleUpload}
                            disabled={!file || isUploading}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                            {isUploading && (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            )}
                            <span>{isUploading ? "Uploading..." : "Upload Suppliers"}</span>
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkSupplierUploadModal;
